<template>
  <div class="space-y-12">
    <div class="bg-white rounded border border-gray-400 overflow-hidden">
      <div class="flex space-x-4 items-center p-4">
        <BaseFormCheckbox label="By collection" v-model="groupByCollection" />
        <BaseFormCheckbox label="View Approved" checked-value="approved" v-model="includeAssets" />
        <BaseFormCheckbox label="View Rejected" checked-value="rejected" v-model="includeAssets" />
        <BaseFormCheckbox
          label="View In Review"
          checked-value="in-review"
          v-model="includeAssets"
        />
        <BaseButton primary @click="openUploadDialog"> Create new Asset </BaseButton>
      </div>
      <div v-if="!loading && hasProjectAssets" class="bg-gray-200 p-4 space-x-2">
        <BaseButton v-if="allSelected" primary small @click="desectAll"> Deselect all </BaseButton>
        <BaseButton v-else primary small @click="selectAll"> Select all </BaseButton>
        <BaseButton v-if="anySelected" primary small @click="handleEditMultiple">
          Edit collection
        </BaseButton>
        <EmailReviewersAction :project="project" />
      </div>
    </div>

    <Loader v-if="loading" />

    <div key="with-grouping" class="space-y-8" v-else-if="groupByCollection">
      <ProjectAssetGroup
        v-if="projectAssetsWithoutCollection.length"
        title="no collection"
        :project-assets="projectAssetsWithoutCollection"
        :selected.sync="selectedAssets"
        :can-manage="canManage"
        @manage-reviews="handleManageReviews"
        @review="handleReview"
        @edit="handleEdit"
        @edit-multiple="handleEditMultiple"
        @edit-link="handleEditLink"
        @view-note="handleViewNote"
        @edit-note="handleEditNote"
      />

      <ProjectAssetGroup
        v-for="(projectAssetsGroup, groupName) in projectAssetsGroupByCollection"
        :key="groupName"
        :title="groupName"
        :project-assets="projectAssetsGroup"
        :selected.sync="selectedAssets"
        :can-manage="canManage"
        @manage-reviews="handleManageReviews"
        @review="handleReview"
        @edit="handleEdit"
        @edit-multiple="handleEditMultiple"
        @edit-link="handleEditLink"
        @view-note="handleViewNote"
        @edit-note="handleEditNote"
      />
    </div>

    <ProjectAssetGroup
      v-else
      :project-assets="projectAssets"
      :selected.sync="selectedAssets"
      :can-manage="canManage"
      @manage-reviews="handleManageReviews"
      @review="handleReview"
      @edit="handleEdit"
      @edit-multiple="handleEditMultiple"
      @edit-link="handleEditLink"
      @view-note="handleViewNote"
      @edit-note="handleEditNote"
    />

    <portal to="app-modal">
      <AddAssetsModal
        v-if="uploadDialogVisible"
        :account-id="project.accountUuid"
        :project-id="projectId"
        @closed="uploadDialogVisible = false"
      />
      <EditProjectAssetModal
        v-if="editModalVisible"
        :project-asset-id="currentProjectAssetId"
        @closed="handleEditModalClosed"
      />
      <EditProjectAssetsModal
        v-if="editMultipleModalVisible"
        :project-id="projectId"
        :project-asset-ids="selectedAssets"
        @closed="handleEditMultipleModalClosed"
      />
      <EditLinkModal
        v-if="editLinkModalVisible"
        :project-asset-id="currentProjectAssetId"
        @closed="handleEditLinkModalClosed"
      />
      <EditNoteModal
        v-if="editNoteModalVisible"
        can-edit
        :project-asset-id="currentProjectAssetId"
        @closed="handleEditNoteModalClosed"
      />
      <EditNoteModal
        v-if="viewNoteModalVisible"
        :project-asset-id="currentProjectAssetId"
        @closed="handleViewNoteModalClosed"
      />
      <ManageAssetReviewsModal
        v-if="manageReviewsModalVisible"
        :project-asset-id="currentProjectAssetId"
        @closed="handleManageReviewsClosed"
      />
      <ManageAssetReviewModal
        v-if="reviewModalVisible"
        :project-asset-id="currentProjectAssetId"
        @closed="handleReviewClosed"
      />
    </portal>
  </div>
</template>

<script>
import AddAssetsModal from '@/components/ProjectAssets/AddAssetsModal.vue';
import EditProjectAssetModal from '@/components/ProjectAssets/EditProjectAssetModal.vue';
import EditProjectAssetsModal from '@/components/ProjectAssets/EditProjectAssetsModal.vue';
import EditLinkModal from '@/components/ProjectAssets/EditLinkModal.vue';
import EditNoteModal from '@/components/ProjectAssets/EditNoteModal.vue';
import ManageAssetReviewsModal from '@/components/ProjectAssets/ManageAssetReviewsModal.vue';
import ManageAssetReviewModal from '@/components/ProjectAssets/ManageAssetReviewModal.vue';
import ProjectAssetGroup from '@/components/ProjectAssets/ProjectAssetGroup.vue';
import EmailReviewersAction from './assets/components/EmailReviewersAction.vue';

export default {
  name: 'Assets',

  components: {
    AddAssetsModal,
    ManageAssetReviewsModal,
    ManageAssetReviewModal,
    ProjectAssetGroup,
    EditProjectAssetModal,
    EditProjectAssetsModal,
    EditLinkModal,
    EditNoteModal,
    EmailReviewersAction,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
    projectId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      groupByCollection: true,
      includeAssets: ['approved', 'rejected', 'in-review'],
      selectedAssets: [],
      uploadDialogVisible: false,
      manageReviewsModalVisible: false,
      reviewModalVisible: false,
      editModalVisible: false,
      editMultipleModalVisible: false,
      editLinkModalVisible: false,
      editNoteModalVisible: false,
      viewNoteModalVisible: false,
      currentProjectAssetId: null,
    };
  },

  computed: {
    project() {
      return this.$store.state.entities.projects.byId[this.projectId];
    },
    canManage() {
      if (this.$isAdminOrSuperAdmin) {
        return true;
      }

      if (!this.project) {
        return false;
      }

      if (this.project.ownerId === this.$user.id) {
        return true;
      }

      return false;
    },
    projectAssets() {
      return this.$store.getters['ui/projectProjectAssets/projectAssetsByProject'](this.projectId);
    },
    includedProjectAssets() {
      return this.projectAssets.filter((projectAsset) => {
        const {
          pendingApprovalRequestsCount,
          rejectedApprovalRequestsCount,
          approvedApprovalRequestsCount,
        } = projectAsset;

        if (pendingApprovalRequestsCount) {
          return this.includeAssets.indexOf('in-review') >= 0;
        }

        if (rejectedApprovalRequestsCount) {
          return this.includeAssets.indexOf('rejected') >= 0;
        }

        if (approvedApprovalRequestsCount) {
          return this.includeAssets.indexOf('approved') >= 0;
        }

        return true;
      });
    },
    projectAssetsWithoutCollection() {
      return this.includedProjectAssets.filter((projectAsset) => !projectAsset.collectionName);
    },
    projectAssetsWithCollection() {
      return this.includedProjectAssets.filter((projectAsset) => projectAsset.collectionName);
    },
    projectAssetsGroupByCollection() {
      return this.projectAssetsWithCollection.reduce(
        (carry, projectAsset) => ({
          ...carry,
          [projectAsset.collectionName]: [
            ...(carry[projectAsset.collectionName] || []),
            projectAsset,
          ],
        }),
        {}
      );
    },
    hasProjectAssets() {
      return this.projectAssets.length > 0;
    },
    allSelected() {
      return this.selectedAssets.length === this.projectAssets.length;
    },
    noneSelected() {
      return this.selectedAssets.length === 0;
    },
    anySelected() {
      return this.selectedAssets.length > 0;
    },
  },

  created() {
    this.loadProjectAssets();
  },

  destroyed() {
    this.$store.dispatch({
      type: 'ui/projectProjectAssets/clear',
      projectId: this.projectId,
    });
  },

  methods: {
    selectAll() {
      this.selectedAssets = this.projectAssets.map((p) => p.id);
    },
    desectAll() {
      this.selectedAssets = [];
    },
    selectAllInCollection(collection = null) {
      this.selectedAssets = this.projectAssets
        .filter((p) => p.collection === collection)
        .map((p) => p.id);
    },
    deselectAllInCollection(collection = null) {
      const targetIds = this.projectAssets
        .filter((p) => p.collection === collection)
        .map((p) => p.id);
      this.selectedAssets = [...this.selectedAssets.filter((id) => targetIds.indexOf(id) === -1)];
    },
    noneSelectedInCollection(collection = null) {
      return this.projectAssets
        .filter((p) => p.collection === collection)
        .map((p) => p.id)
        .every((id) => this.selectedAssets.indexOf(id) === -1);
    },
    anySelectedInCollection(collection = null) {
      return this.projectAssets
        .filter((p) => p.collection === collection)
        .map((p) => p.id)
        .some((id) => this.selectedAssets.indexOf(id) > -1);
    },
    allSelectedInCollection(collection = null) {
      return this.projectAssets
        .filter((p) => p.collection === collection)
        .map((p) => p.id)
        .every((id) => this.selectedAssets.indexOf(id) > -1);
    },
    async handleProjectAssetDelete(projectAsset) {
      try {
        await this.$store.dispatch({
          type: 'ui/projectProjectAssets/destroy',
          projectId: this.projectId,
          projectAssetId: projectAsset.id,
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.reject();
      }
    },
    handleManageReviews(projectAsset) {
      this.currentProjectAssetId = projectAsset.id;
      this.manageReviewsModalVisible = true;
    },
    handleManageReviewsClosed() {
      this.manageReviewsModalVisible = false;
      this.currentProjectAssetId = null;
    },
    handleEdit(projectAsset) {
      this.currentProjectAssetId = projectAsset.id;
      this.editModalVisible = true;
    },
    handleEditModalClosed() {
      this.editModalVisible = false;
      this.currentProjectAssetId = null;
    },
    handleEditMultiple() {
      this.editMultipleModalVisible = true;
    },
    handleEditMultipleModalClosed() {
      this.editMultipleModalVisible = false;
    },
    handleEditLink(projectAsset) {
      this.currentProjectAssetId = projectAsset.id;
      this.editLinkModalVisible = true;
    },
    handleEditLinkModalClosed() {
      this.editLinkModalVisible = false;
      this.currentProjectAssetId = null;
    },
    handleEditNote(projectAsset) {
      this.currentProjectAssetId = projectAsset.id;
      this.editNoteModalVisible = true;
    },
    handleEditNoteModalClosed() {
      this.editNoteModalVisible = false;
      this.currentProjectAssetId = null;
    },
    handleViewNote(projectAsset) {
      this.currentProjectAssetId = projectAsset.id;
      this.viewNoteModalVisible = true;
    },
    handleViewNoteModalClosed() {
      this.viewNoteModalVisible = false;
      this.currentProjectAssetId = null;
    },
    handleReview(projectAsset) {
      this.currentProjectAssetId = projectAsset.id;
      this.reviewModalVisible = true;
    },
    handleReviewClosed() {
      this.reviewModalVisible = false;
      this.currentProjectAssetId = null;
    },
    async loadProjectAssets(params = {}) {
      try {
        this.loading = true;

        await this.$store.dispatch({
          type: 'ui/projectProjectAssets/load',
          projectId: this.projectId,
          params: {
            ...params,
            include: [
              'asset.assetable',
              'asset.projectTask.task',
              'approvalRequestsCount',
              'approvalClaimsCount',
              'approvedApprovalRequestsCount',
              'rejectedApprovalRequestsCount',
              'pendingApprovalRequestsCount',
              'pendingApprovalRequest',
            ].join(','),
          },
        });
      } catch (error) {
        // handle error
      } finally {
        this.loading = false;
      }
    },
    openUploadDialog() {
      this.uploadDialogVisible = true;
    },
  },
};
</script>
